import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalkInPage = () => {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ client_name: "", client_phone: "", client_email: "", service_name: "", hair_concerns: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showRebook, setShowRebook] = useState(false);
  const [rebookDate, setRebookDate] = useState("");
  const [rebookTime, setRebookTime] = useState("");
  const [rebooking, setRebooking] = useState(false);
  const [rebooked, setRebooked] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch((e) => console.error("Failed to load services:", e));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.client_phone || !form.service_name) {
      toast.error("Please fill in name, phone, and service");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/walkin`, form, { withCredentials: true });
      toast.success("Walk-in client registered!");
      setSubmitted(true);
    } catch (err) {
      toast.error("Failed to register");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSubmitted(false);
    setShowRebook(false);
    setRebooked(false);
    setRebookDate("");
    setRebookTime("");
    setForm({ client_name: "", client_phone: "", client_email: "", service_name: "", hair_concerns: "", notes: "" });
  };

  const handleRebook = async () => {
    if (!rebookDate || !rebookTime) {
      toast.error("Please select a date and time");
      return;
    }
    setRebooking(true);
    try {
      await axios.post(`${API}/bookings`, {
        clientName: form.client_name,
        clientEmail: form.client_email || "",
        clientPhone: form.client_phone,
        style: form.service_name,
        date: rebookDate,
        time: rebookTime,
        notes: "Rebooked from walk-in",
        sms_consent: form.sms_consent || false,
      });
      toast.success("Next appointment booked!");
      setRebooked(true);
    } catch (err) {
      toast.error("Failed to book. Try again.");
    } finally {
      setRebooking(false);
    }
  };

  const getTimeSlots = () => {
    const slots = [];
    for (let h = 6; h <= 20; h++) {
      const hour = h > 12 ? h - 12 : h;
      const ampm = h >= 12 ? "PM" : "AM";
      slots.push(`${hour}:00 ${ampm}`);
      if (h < 20) slots.push(`${hour}:30 ${ampm}`);
    }
    return slots;
  };

  const getMinDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col" data-testid="walkin-page">
      <header className="border-b border-brand-border bg-brand-surface px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="font-serif text-xl text-brand-text" data-testid="walkin-admin-link">
            Afro <span className="text-brand-gold">Magic</span>
          </Link>
          <span className="text-xs font-sans text-brand-gold bg-brand-gold/10 px-2 py-0.5 border border-brand-gold/20">Walk-In</span>
        </div>
        <Link to="/admin" className="flex items-center gap-1 font-sans text-xs text-brand-text-muted hover:text-brand-gold"><ArrowLeft size={14} /> Admin</Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        {submitted ? (
          <div className="w-full max-w-md" data-testid="walkin-success">
            {rebooked ? (
              <div className="text-center">
                <CheckCircle size={48} className="text-brand-gold mx-auto mb-4" />
                <h2 className="font-serif text-2xl text-brand-text mb-2">Next Appointment Booked!</h2>
                <p className="font-sans text-sm text-brand-text-muted mb-2">{form.client_name}'s next visit:</p>
                <p className="font-sans text-lg text-brand-gold font-semibold mb-1">{new Date(rebookDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
                <p className="font-sans text-sm text-brand-text mb-6">{rebookTime} — {form.service_name}</p>
                <button onClick={reset} data-testid="walkin-new-btn" className="bg-brand-gold text-black font-sans font-semibold text-sm px-8 py-3 hover:bg-brand-gold-light transition-colors">
                  Register Another Walk-In
                </button>
              </div>
            ) : (
              <div className="text-center">
                <CheckCircle size={48} className="text-brand-gold mx-auto mb-4" />
                <h2 className="font-serif text-2xl text-brand-text mb-2">Client Registered!</h2>
                <p className="font-sans text-sm text-brand-text-muted mb-6">Walk-in has been added to today's bookings.</p>

                {!showRebook ? (
                  <div className="space-y-3">
                    <button onClick={() => setShowRebook(true)} data-testid="walkin-rebook-btn" className="w-full bg-brand-gold text-black font-sans font-semibold text-sm px-8 py-3 hover:bg-brand-gold-light transition-colors">
                      Book Next Appointment for {form.client_name}
                    </button>
                    <button onClick={reset} data-testid="walkin-new-btn" className="w-full border border-brand-border text-brand-text-muted font-sans text-sm px-8 py-3 hover:border-brand-gold hover:text-brand-gold transition-colors">
                      Register Another Walk-In
                    </button>
                  </div>
                ) : (
                  <div className="text-left space-y-4 border border-brand-border bg-brand-surface p-5" data-testid="walkin-rebook-form">
                    <p className="font-sans text-xs text-brand-gold font-semibold uppercase tracking-wider">Book Next Visit for {form.client_name}</p>
                    <p className="font-sans text-xs text-brand-text-muted">Schedule their next appointment before they leave</p>
                    <div>
                      <label className="font-sans text-xs text-brand-text-muted block mb-1">Date</label>
                      <input
                        type="date"
                        value={rebookDate}
                        onChange={(e) => setRebookDate(e.target.value)}
                        min={getMinDate()}
                        data-testid="walkin-rebook-date"
                        className="w-full bg-brand-bg border border-brand-border text-brand-text px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-gold"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-xs text-brand-text-muted block mb-1">Time</label>
                      <select
                        value={rebookTime}
                        onChange={(e) => setRebookTime(e.target.value)}
                        data-testid="walkin-rebook-time"
                        className="w-full bg-brand-bg border border-brand-border text-brand-text px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-gold"
                      >
                        <option value="">Select time</option>
                        {getTimeSlots().map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <p className="font-sans text-xs text-brand-text-muted">Service: <span className="text-brand-gold">{form.service_name}</span></p>
                    <div className="flex gap-2">
                      <button onClick={() => setShowRebook(false)} className="flex-1 py-2.5 border border-brand-border text-brand-text-muted font-sans text-sm">Cancel</button>
                      <button onClick={handleRebook} disabled={rebooking} data-testid="walkin-rebook-confirm" className="flex-1 py-2.5 bg-brand-gold text-black font-sans font-semibold text-sm hover:bg-brand-gold-light disabled:opacity-50">
                        {rebooking ? "Booking..." : "Confirm Booking"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="font-serif text-3xl text-brand-text">Walk-In Client</h1>
              <p className="font-sans text-sm text-brand-text-muted mt-2">Fill in client info before starting their service</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="walkin-form">
              <div>
                <label className="font-sans text-xs text-brand-text-muted block mb-1">Client Name *</label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Full name" data-testid="walkin-name" className="bg-brand-surface border-brand-border text-brand-text rounded-none" />
              </div>
              <div>
                <label className="font-sans text-xs text-brand-text-muted block mb-1">Phone Number *</label>
                <Input type="tel" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="(313) 555-1234" data-testid="walkin-phone" className="bg-brand-surface border-brand-border text-brand-text rounded-none" />
              </div>
              <div>
                <label className="font-sans text-xs text-brand-text-muted block mb-1">Email (optional)</label>
                <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="client@email.com" data-testid="walkin-email" className="bg-brand-surface border-brand-border text-brand-text rounded-none" />
              </div>
              <div>
                <label className="font-sans text-xs text-brand-text-muted block mb-1">Service *</label>
                <Select value={form.service_name} onValueChange={(val) => setForm({ ...form, service_name: val })}>
                  <SelectTrigger className="bg-brand-surface border-brand-border text-brand-text rounded-none" data-testid="walkin-service">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.name}>{s.name} - ${s.starting_price}</SelectItem>
                    ))}
                    <SelectItem value="Custom Style">Custom Style</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-sans text-xs text-brand-text-muted block mb-1">Hair Concerns / Allergies</label>
                <Textarea value={form.hair_concerns} onChange={(e) => setForm({ ...form, hair_concerns: e.target.value })} placeholder="Any sensitivities, scalp conditions, allergies..." data-testid="walkin-concerns" className="bg-brand-surface border-brand-border text-brand-text rounded-none min-h-[80px]" />
              </div>
              <div>
                <label className="font-sans text-xs text-brand-text-muted block mb-1">Additional Notes</label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Preferred length, color, etc." data-testid="walkin-notes" className="bg-brand-surface border-brand-border text-brand-text rounded-none" />
              </div>

              {/* SMS Consent */}
              <label className="flex items-start gap-3 cursor-pointer" data-testid="walkin-sms-consent">
                <input
                  type="checkbox"
                  checked={form.sms_consent || false}
                  onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-[#D4AF37] cursor-pointer flex-shrink-0"
                />
                <span className="font-sans text-xs text-brand-text-muted leading-relaxed">
                  I agree to receive automated appointment reminders and service updates via SMS from Afro Magic Braiding. Message and data rates may apply. Reply STOP to opt-out, HELP for help. <a href="/consent-policy" target="_blank" className="text-brand-gold underline">Privacy Policy</a>
                </span>
              </label>

              <button type="submit" disabled={loading} data-testid="walkin-submit" className="w-full py-3 bg-brand-gold text-black font-sans font-semibold text-sm hover:bg-brand-gold-light transition-colors disabled:opacity-60">
                {loading ? "Registering..." : "Register Walk-In Client"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalkInPage;
